import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Rect, Text, Group, Line, Ellipse } from 'react-konva';
import Konva from 'konva';
import { Node } from '../../types';
import { IconStyle, ThemePalette, SocketLabelVisibility, KonvaMouseEvent, KonvaDragEvent } from '../../types';
import { SocketComponent } from './SocketComponent';
import { NodeIcon } from './NodeIcon';
import { calcNodeHeight, ghostSocketX, ghostSocketY, ENDPOINT_ROW } from '../../utils/socketLayout';
import { GhostSocket } from './GhostSocket';
import { ResizeHandle } from './ResizeHandle';
import { NodeActionBar } from './NodeActionBar';
import { NodeBody } from './NodeBody';
import { FONT_FAMILY, scaleFont } from '../../constants/typography';
import { UML_CLASSIFIER_TYPES, isKitShape, isMarkupType, isMagneticNode, isSketchShape, locksEqualAspect } from '../../constants/nodeTypes';
import { isNearNodeBorder, magneticBorderThreshold } from '../../utils/magneticConnect';
import { isResizeKeepout, showsIdleResizeHint } from '../../utils/resizeHandle';
import { constrainedResize, sketchLabelBox, SKETCH_LABEL, usesWrappedCenteredLabel } from '../../utils/sketchLabel';
import { isDrawCascadeModifier, isStackableDrawing, symmetricHandleSize } from '../../utils/drawStack';
import { MarkupNodeComponent } from './MarkupNodeComponent';
import { HaloText, LABEL_HALO_ALPHA } from './HaloText';
import { toRgba } from '../../utils/color';
import { classifierBoxSize, classifierLines } from '../../utils/umlMembers';

// Node types that render a custom non-rectangular body
const SHAPED_TYPES = new Set(['diamond', 'database', 'queue', 'box', 'oval', 'rounded', 'cylinder', 'hexagon', 'triangle', 'cross',
  'net_cloud', 'net_subnet', 'net_host', 'net_firewall', 'net_router', 'net_lb', 'net_switch',
  'pipe_source', 'pipe_process', 'pipe_store', 'pipe_sink', 'pipe_queue',
  'uml_class', 'uml_interface', 'uml_enum', 'uml_actor', 'uml_package']);

interface NodeComponentProps {
  node: Node;
  onUpdate: (id: string, updates: Partial<Node>) => void;
  connectedSockets: Set<string>;
  socketRoles?: Map<string, 'source' | 'target'>;
  isConnecting: boolean;
  isSelected: boolean;
  onSocketMouseDown: (nodeId: string, socketId: string) => void;
  onSocketMouseUp: (nodeId: string, socketId: string) => void;
  onSocketDoubleClick?: (nodeId: string, socketId: string) => void;
  onSocketSlideStart?: (nodeId: string, socketId: string) => void;
  onNodeClick: (nodeId: string, shiftKey?: boolean) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
  onLabelDoubleClick?: (nodeId: string) => void;
  onDescDoubleClick?: (nodeId: string) => void;
  onNodeContextMenu?: (nodeId: string, x: number, y: number) => void;
  onNodeResize?: (nodeId: string, width: number, height: number, options?: { fromCenter?: boolean; cascade?: boolean }) => void;
  onNodeHover?: (nodeId: string | null) => void;
  layerOpacity?: number;
  isAnchor?: boolean;
  iconStyle?: IconStyle;
  palette: ThemePalette;
  hideLabel?: boolean;
  editingSocketId?: string | null;
  socketLabelVisibility?: SocketLabelVisibility;
  // Multi-select drag support
  onMultiDragStart?: (nodeId: string) => void;
  onMultiDragMove?: (nodeId: string, dx: number, dy: number) => void;
  onMultiDragEnd?: (nodeId: string, dx: number, dy: number) => void;
  // Waypoint drag callbacks (canvas pos, separate from onUpdate which fires on both move+end)
  onWaypointDragMove?: (nodeId: string, canvasX: number, canvasY: number) => void;
  onWaypointDragEnd?: (nodeId: string, canvasX: number, canvasY: number) => void;
  // Action bar callbacks
  onActionInfo?: (nodeId: string) => void;
  onActionColor?: (nodeId: string) => void;
  onActionEnterGroup?: (nodeId: string) => void;
  onActionPortalNavigate?: (nodeId: string) => void;
  onActionLinkPage?: (nodeId: string) => void;
  isReadOnly?: boolean;
  canDrag?: boolean;
  // Ghost socket — shown on eligible nodes while a connection drag is active
  connectionSourceSocketType?: 'input' | 'output' | null;
  connectionSourceNodeId?: string | null;
  onGhostSocketDrop?: (nodeId: string, ghostType: 'input' | 'output') => void;
  onMagneticConnectStart?: (nodeId: string, localX: number, localY: number) => boolean | void;
  onMagneticConnectDrop?: (nodeId: string) => void;
  /** Hold E to start a connection from the node body. */
  connectModifierHeldRef?: React.MutableRefObject<boolean>;
  /** The page's label multiplier. 1 leaves every size exactly as authored. */
  textScale?: number;
}

export const NodeComponent: React.FC<NodeComponentProps> = (props) => {
  if (isMarkupType(props.node.type)) {
    return (
      <MarkupNodeComponent
        node={props.node}
        isSelected={props.isSelected}
        isReadOnly={props.isReadOnly}
        canDrag={props.canDrag}
        hideLabel={props.hideLabel}
        layerOpacity={props.layerOpacity}
        palette={props.palette}
        onUpdate={props.onUpdate}
        onNodeClick={props.onNodeClick}
        onNodeDoubleClick={props.onNodeDoubleClick}
        onLabelDoubleClick={props.onLabelDoubleClick}
        onNodeContextMenu={props.onNodeContextMenu}
        onNodeHover={props.onNodeHover}
        onNodeResize={props.onNodeResize}
        onMultiDragStart={props.onMultiDragStart}
        onMultiDragMove={props.onMultiDragMove}
        onMultiDragEnd={props.onMultiDragEnd}
      />
    );
  }
  return <ArchitectureNodeComponent {...props} />;
};

const ArchitectureNodeComponent: React.FC<NodeComponentProps> = ({
  node,
  onUpdate,
  connectedSockets,
  socketRoles,
  isConnecting,
  isSelected,
  onSocketMouseDown,
  onSocketMouseUp,
  onSocketDoubleClick,
  onSocketSlideStart,
  onNodeClick,
  onNodeDoubleClick,
  onLabelDoubleClick,
  onDescDoubleClick,
  onNodeContextMenu,
  onNodeResize,
  onNodeHover,
  layerOpacity = 1,
  isAnchor = false,
  iconStyle = 'badge',
  palette,
  hideLabel = false,
  editingSocketId = null,
  socketLabelVisibility = 'always',
  onMultiDragStart,
  onMultiDragMove,
  onMultiDragEnd,
  onActionInfo,
  onActionColor,
  onWaypointDragMove,
  onWaypointDragEnd,
  onActionEnterGroup,
  onActionPortalNavigate,
  onActionLinkPage,
  isReadOnly = false,
  canDrag,
  connectionSourceSocketType = null,
  connectionSourceNodeId = null,
  onGhostSocketDrop,
  onMagneticConnectStart,
  onMagneticConnectDrop,
  connectModifierHeldRef,
  textScale = 1,
}) => {

  const w = node.size.width;
  const h = node.size.height;
  /**
   * Labels the node owns outright scale with the page. Rows whose pitch was
   * fixed elsewhere - classifier members, endpoint lists, the description
   * section - keep their authored size, since growing the text alone would push
   * it out of the strip it was measured into.
   */
  const fs = (px: number) => scaleFont(px, textScale);
  const isBoundaryNode = node.type === 'group_input' || node.type === 'group_output';
  const isGroupNode = node.type === 'group' || !!node.childGraphId;
  const isWaypoint = node.type === 'waypoint';
  
  // Determine accent color: boundary nodes are always neutral grey, group nodes use their data color
  const accentColor = isBoundaryNode
    ? '#64748b'
    : isGroupNode
      ? (node.data?.color ?? '#64748b')
      : (node.data?.color ?? '#6366f1');
  
  // Determine type label
  const typeLabel = isBoundaryNode
    ? (node.type === 'group_input' ? 'GROUP INPUT' : 'GROUP OUTPUT')
    : isGroupNode
      ? 'GROUP'
      : node.type.charAt(0).toUpperCase() + node.type.slice(1);
  
  const isShaped = SHAPED_TYPES.has(node.type);
  const isSketchShapeNode = isSketchShape(node.type);
  const isMagneticShapeNode = isMagneticNode(node.type);
  const isClassifier = UML_CLASSIFIER_TYPES.has(node.type);
  const nodeDirection = (!isShaped && node.data?.direction) || 'ltr';

  useEffect(() => {
    if (!isClassifier || !onNodeResize) return;
    const fitted = classifierBoxSize(node);
    if (fitted.height > h + 1 || fitted.width > w + 1) {
      onNodeResize(node.id, Math.max(w, fitted.width), Math.max(h, fitted.height));
    }
  }, [
    isClassifier,
    h,
    w,
    node.id,
    node.label,
    node.data?.stereotype,
    node.data?.attributes,
    node.data?.methods,
    onNodeResize,
  ]);

  // Ghost socket eligibility — rectangular non-TTB nodes only, not the source node itself
  const GHOST_EXCLUDED_TYPES = new Set([
    'diamond', 'database', 'queue', 'box', 'oval', 'rounded', 'cylinder', 'hexagon', 'triangle', 'cross',
    'group_input', 'group_output', 'waypoint', 'decision',
    ...['net_cloud', 'net_subnet', 'net_host', 'net_firewall', 'net_router', 'net_lb', 'net_switch',
      'pipe_source', 'pipe_process', 'pipe_store', 'pipe_sink', 'pipe_queue',
      'uml_class', 'uml_interface', 'uml_enum', 'uml_actor', 'uml_package'],
  ]);
  const ghostType: 'input' | 'output' | null = (() => {
    if (!connectionSourceSocketType || !isConnecting) return null;
    if (GHOST_EXCLUDED_TYPES.has(node.type)) return null;
    if (node.id === connectionSourceNodeId) return null;
    const dir = node.data?.direction ?? 'ltr';
    if (dir === 'ttb' || dir === 'btt') return null;
    return connectionSourceSocketType === 'output' ? 'input' : 'output';
  })();

  const ghostX = (() => {
    if (!ghostType) return 0;
    const dir = node.data?.direction ?? 'ltr';
    const onRight = ghostType === 'input' ? dir === 'rtl' : dir !== 'rtl';
    return ghostSocketX(onRight ? 'right' : 'left', w);
  })();
  const ghostY = ghostType
    ? ghostSocketY(node.sockets.filter(s => s.type === ghostType).length, h)
    : 0;

  const [isHovered, setIsHovered] = useState(false);

  const hasLinkedPage = !!node.linkedPageId;
  const showActionBar = !isReadOnly && (isSelected || isHovered) && !isBoundaryNode && !isWaypoint;
  const magneticRim = magneticBorderThreshold(w, h);
  const showResizeHandle = !isReadOnly && !!onNodeResize && !isConnecting;
  const canDragNode = canDrag ?? (!isConnecting && !isReadOnly);
  const groupRef = useRef<Konva.Group>(null);

  useEffect(() => {
    if (!groupRef.current) return;
    groupRef.current.opacity(0);
    groupRef.current.scale({ x: 0.85, y: 0.85 });
    const tw = new Konva.Tween({
      node: groupRef.current,
      duration: 0.15,
      opacity: layerOpacity,
      scaleX: 1,
      scaleY: 1,
      easing: Konva.Easings.EaseOut,
    });
    tw.play();
    return () => tw.destroy();
  }, [layerOpacity]);

  // Track original size at resize drag start (for Alt proportional resize)
  const resizeOrigin = useRef<{ w: number; h: number } | null>(null);

  const nIn = node.sockets.filter(s => s.type === 'input').length;
  const nOut = node.sockets.filter(s => s.type === 'output').length;
  const endpoints: string[] = (node.type === 'api' && Array.isArray(node.data?.endpoints)) ? node.data.endpoints : [];
  const baseH = calcNodeHeight(nIn, nOut, endpoints.length);
  const hasDesc = !!(node.meta?.description) && !isShaped; // desc section disabled for shaped nodes
  const descSectionHeight = Math.max(0, h - baseH);
  const DESC_TOP_PAD = 8;
  const DESC_BOTTOM_PAD = 6;
  const MIN_DESC_TEXT_HEIGHT = 12; // roughly one readable line at 10px font
  const hasRoomForDescSection =
    hasDesc && descSectionHeight >= (DESC_TOP_PAD + MIN_DESC_TEXT_HEIGHT + DESC_BOTTOM_PAD);
  const descTextHeight = Math.max(0, descSectionHeight - DESC_TOP_PAD - DESC_BOTTOM_PAD);

  // Track drag origin for multi-select delta computation
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const pendingMagneticConnect = useRef(false);

  const pointerLocal = (e: KonvaMouseEvent | KonvaDragEvent) => {
    const pointer = e.target.getStage()?.getPointerPosition();
    if (!pointer || !groupRef.current) return null;
    const transform = groupRef.current.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(pointer);
  };

  const handleDragStart = useCallback((e: KonvaDragEvent) => {
    if (isConnecting || pendingMagneticConnect.current) {
      pendingMagneticConnect.current = false;
      e.target.stopDrag();
      return;
    }
    const local = pointerLocal(e);
    if (local && showResizeHandle && isResizeKeepout(local.x, local.y, w, h)) {
      e.target.stopDrag();
      return;
    }
    dragOrigin.current = { x: e.target.x(), y: e.target.y() };
    onMultiDragStart?.(node.id);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grabbing';
  }, [isConnecting, node.id, onMultiDragStart, w, h, showResizeHandle]);

  const handleDragMove = useCallback((e: KonvaDragEvent) => {
    const x = e.target.x();
    const y = e.target.y();
    onUpdate(node.id, { position: { x, y } });
    if (onMultiDragMove && dragOrigin.current) {
      onMultiDragMove(node.id, x - dragOrigin.current.x, y - dragOrigin.current.y);
    }
  }, [node.id, onUpdate, onMultiDragMove]);

  const handleDragEnd = useCallback((e: KonvaDragEvent) => {
    const x = e.target.x();
    const y = e.target.y();
    onUpdate(node.id, { position: { x, y } });
    if (onMultiDragEnd && dragOrigin.current) {
      const dx = x - dragOrigin.current.x;
      const dy = y - dragOrigin.current.y;
      dragOrigin.current = null;
      onMultiDragEnd(node.id, dx, dy);
    }
    dragOrigin.current = null;
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'default';
  }, [node.id, onUpdate, onMultiDragEnd]);

  const handleClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onNodeClick(node.id, e.evt?.shiftKey ?? false);
  }, [node.id, onNodeClick]);

  const handleMouseEnter = useCallback((e: KonvaMouseEvent) => {
    setIsHovered(true);
    onNodeHover?.(node.id);
    if (isConnecting || !canDragNode) return;
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grab';
  }, [isConnecting, canDragNode, node.id, onNodeHover]);

  const handleMouseLeave = useCallback((e: KonvaMouseEvent) => {
    setIsHovered(false);
    onNodeHover?.(null);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = isConnecting ? 'crosshair' : 'default';
  }, [isConnecting, onNodeHover]);

  const handleContextMenu = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.preventDefault();
    onNodeContextMenu?.(node.id, e.evt.clientX, e.evt.clientY);
  }, [node.id, onNodeContextMenu]);

  const handleDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onNodeDoubleClick?.(node.id);
  }, [node.id, onNodeDoubleClick]);

  const handleLabelDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onLabelDoubleClick?.(node.id);
  }, [node.id, onLabelDoubleClick]);

  const handleDescDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onDescDoubleClick?.(node.id);
  }, [node.id, onDescDoubleClick]);

  const inputs = node.sockets.filter(s => s.type === 'input');
  const outputs = node.sockets.filter(s => s.type === 'output');
  const halfW = w / 2;

  // For shaped nodes: center labels. x=8 + width=w-16 centers text at w/2 in Konva.
  // For rectangular: left-aligned with accent bar offset.
  const labelX = isShaped ? 8 : 14;
  const labelAlign = isShaped ? 'center' : 'left';
  const labelW = isShaped ? w - 16 : w - 42;
  const wrapLabelBox = usesWrappedCenteredLabel(node.type) ? sketchLabelBox(node.size, node.type) : null;
  const labelHalo = toRgba(palette.canvasBg, LABEL_HALO_ALPHA);

  // ── Waypoint: minimal pill, no label, no action bar ──────────────────
  if (isWaypoint) {
    return (
      <Group
        ref={groupRef}
        x={node.position.x}
        y={node.position.y}
        opacity={layerOpacity}
        draggable={canDragNode}
        onDragStart={handleDragStart}
        onDragMove={(e: KonvaDragEvent) => {
          onUpdate(node.id, { position: { x: e.target.x(), y: e.target.y() } });
          onWaypointDragMove?.(node.id, e.target.x(), e.target.y());
        }}
        onDragEnd={(e: KonvaDragEvent) => {
          const cx = e.target.x(); const cy = e.target.y();
          onUpdate(node.id, { position: { x: cx, y: cy } });
          onWaypointDragEnd?.(node.id, cx, cy);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = 'default';
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      >
        {/* Selection ring */}
        {isSelected && (
          <Rect
            x={-4} y={-4} width={w + 8} height={h + 8}
            cornerRadius={16} stroke={accentColor} strokeWidth={1.5}
            dash={[4, 3]} fill="transparent"
            listening={false} perfectDrawEnabled={false}
          />
        )}
        {/* Anchor indicator */}
        {isAnchor && !isSelected && (
          <Rect
            x={-5} y={-5} width={w + 10} height={h + 10}
            cornerRadius={17} stroke={accentColor} strokeWidth={1}
            dash={[3, 3]} fill="transparent" opacity={0.4}
            listening={false} perfectDrawEnabled={false}
          />
        )}
        {/* Body pill */}
        <Rect
          width={w} height={h}
          fill={isHovered || isSelected ? accentColor + '30' : palette.nodeBody}
          stroke={isSelected ? accentColor : isHovered ? accentColor + '80' : palette.nodeStroke}
          strokeWidth={isSelected ? 1.5 : 0.8}
          cornerRadius={Math.max(0, h / 2)}
          perfectDrawEnabled={false}
        />
        {/* Sockets */}
        {node.sockets.map(socket => (
          <SocketComponent
            key={socket.id}
            socket={socket}
            nodeId={node.id}
            isConnected={connectedSockets.has(`${node.id}-${socket.id}`)}
            isConnecting={isConnecting}
            accentColor={accentColor}
            onSocketMouseDown={onSocketMouseDown}
            onSocketMouseUp={onSocketMouseUp}
            onSocketDoubleClick={onSocketDoubleClick}
            palette={palette}
            connectedAs={socketRoles?.get(`${node.id}-${socket.id}`) ?? null}
          />
        ))}
      </Group>
    );
  }

  return (
    <Group
      ref={groupRef}
      x={node.position.x}
      y={node.position.y}
      opacity={layerOpacity}
      draggable={canDragNode}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onClick={handleClick}
      onDblClick={handleDblClick}
      onMouseDown={(e) => {
        if (isReadOnly || !onMagneticConnectStart) return;
        if (isConnecting) return;
        if (e.target.name() === 'resize-handle') return;
        const local = pointerLocal(e);
        if (!local) return;
        const fromBorder = isMagneticShapeNode && isNearNodeBorder(local.x, local.y, w, h, magneticRim)
          && !(showResizeHandle && isResizeKeepout(local.x, local.y, w, h));
        const fromBodyModifier = connectModifierHeldRef?.current || (isMagneticShapeNode && e.evt.altKey);
        if ((fromBorder || fromBodyModifier) && !(showResizeHandle && isResizeKeepout(local.x, local.y, w, h))) {
          const started = onMagneticConnectStart(node.id, local.x, local.y);
          pendingMagneticConnect.current = started !== false;
        }
      }}
      onMouseUp={(e) => {
        if (!isConnecting || !onMagneticConnectDrop) return;
        e.cancelBubble = true;
        onMagneticConnectDrop(node.id);
      }}
      onMouseMove={(e) => {
        if (isConnecting || isReadOnly || (!isMagneticShapeNode && !connectModifierHeldRef?.current)) return;
        const local = pointerLocal(e);
        const container = e.target.getStage()?.container();
        if (!container || !local) return;
        const near = isNearNodeBorder(local.x, local.y, w, h, magneticRim) || e.evt.altKey || !!connectModifierHeldRef?.current;
        if (showResizeHandle && isResizeKeepout(local.x, local.y, w, h)) {
          container.style.cursor = 'nwse-resize';
          return;
        }
        container.style.cursor = near ? 'crosshair' : (canDragNode ? 'grab' : 'default');
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onContextMenu={handleContextMenu}
    >
      {/* AABB hit target so corner chrome (resizer) stays reachable on ovals/diamonds */}
      <Rect
        width={w}
        height={h}
        fill="transparent"
        listening
        perfectDrawEnabled={false}
      />

      {/* Anchor indicator — subtle dotted ring on the node chords will connect from */}
      {isAnchor && !isSelected && (
        <Rect
          x={-6} y={-6} width={w + 12} height={h + 12}
          cornerRadius={16} stroke={accentColor} strokeWidth={1}
          dash={[3, 4]} fill="transparent" opacity={0.35}
          listening={false} perfectDrawEnabled={false}
        />
      )}

      {/* Selection ring */}
      {isSelected && node.type !== 'diamond' && node.type !== 'oval' && node.type !== 'hexagon' && node.type !== 'triangle' && node.type !== 'cross' && (
        <Rect
          x={-4} y={-4}
          width={w + 8} height={h + 8}
          cornerRadius={node.type === 'rounded' ? (h * 0.42 + 4) : 14}
          stroke={accentColor} strokeWidth={1.5}
          dash={[6, 4]} fill="transparent"
          listening={false} perfectDrawEnabled={false}
        />
      )}
      {isSelected && node.type === 'diamond' && (
        <Line
          points={[w / 2, -6, w + 6, h / 2, w / 2, h + 6, -6, h / 2]}
          closed
          stroke={accentColor} strokeWidth={1.5}
          dash={[6, 4]} fill="transparent"
          listening={false} perfectDrawEnabled={false}
        />
      )}
      {isSelected && node.type === 'oval' && (
        <Ellipse
          x={w / 2} y={h / 2}
          radiusX={Math.max(1, w / 2 + 5)} radiusY={Math.max(1, h / 2 + 5)}
          stroke={accentColor} strokeWidth={1.5}
          dash={[6, 4]} fill="transparent"
          listening={false} perfectDrawEnabled={false}
        />
      )}
      {isSelected && node.type === 'hexagon' && (() => {
        const pad = 6;
        const rot = ((Math.round((node.data?.shapeRotation as number) ?? 0) % 180) + 180) % 180;
        if (rot === 90) {
          const oy = h * 0.25;
          const py = oy * 0.7;
          const pts = [w / 2, -pad, w + pad, py, w + pad, h - py, w / 2, h + pad, -pad, h - py, -pad, py];
          return <Line points={pts} closed stroke={accentColor} strokeWidth={1.5} dash={[6, 4]} fill="transparent" listening={false} perfectDrawEnabled={false} />;
        }
        const ox = w * 0.25;
        const pox = ox - pad * 0.5;
        return (
          <Line
            points={[pox, -pad, w - pox, -pad, w + pad, h / 2, w - pox, h + pad, pox, h + pad, -pad, h / 2]}
            closed stroke={accentColor} strokeWidth={1.5}
            dash={[6, 4]} fill="transparent"
            listening={false} perfectDrawEnabled={false}
          />
        );
      })()}
      {isSelected && node.type === 'triangle' && (() => {
        const rot = ((Math.round((node.data?.shapeRotation as number) ?? 0) % 360) + 360) % 360;
        let pts: number[];
        if (rot === 90)       pts = [-5, -6, w + 6, -6, w / 2, h + 6];
        else if (rot === 180) pts = [w + 5, -6, w + 5, h + 6, -6, h / 2];
        else if (rot === 270) pts = [-5, h + 6, w + 6, h + 6, w / 2, -6];
        else                  pts = [-5, -6, -5, h + 6, w + 6, h / 2];
        return (
          <Line
            points={pts}
            closed stroke={accentColor} strokeWidth={1.5}
            dash={[6, 4]} fill="transparent"
            listening={false} perfectDrawEnabled={false}
          />
        );
      })()}
      {isSelected && node.type === 'cross' && (
        <Rect
          x={-4} y={-4}
          width={w + 8} height={h + 8}
          cornerRadius={6}
          stroke={accentColor} strokeWidth={1.5}
          dash={[6, 4]} fill="transparent"
          listening={false} perfectDrawEnabled={false}
        />
      )}

      {/* ── Shaped node body ──────────────────────────────────── */}
      {isShaped ? (
        <NodeBody
          type={node.type} w={w} h={h}
          accentColor={accentColor} isSelected={isSelected} palette={palette}
          diamondRotation={node.data?.diamondRotation as number | undefined}
          shapeRotation={(node.data?.shapeRotation as number) ?? 0}
        />
      ) : (
        <>
          {/* Drop shadow */}
          <Rect
            x={2} y={4} width={w} height={h}
            cornerRadius={10} fill={palette.nodeShadow}
            opacity={isBoundaryNode ? 0.4 : 1}
            listening={false} perfectDrawEnabled={false}
          />

          {/* Main body */}
          <Rect
            width={w} height={h}
            fill={isBoundaryNode ? '#1e293b' : palette.nodeBody}
            stroke={isSelected ? accentColor : palette.nodeStroke}
            strokeWidth={isSelected ? 1.5 : 1}
            cornerRadius={10}
            perfectDrawEnabled={false}
          />

          {/* Accent bar — position rotates with node direction */}
          {nodeDirection === 'ttb' ? (
            <Rect
              x={0} y={0} width={w} height={5}
              fill={accentColor}
              cornerRadius={[10, 10, 0, 0]}
              opacity={isBoundaryNode ? 0.5 : 1}
              listening={false} perfectDrawEnabled={false}
            />
          ) : nodeDirection === 'rtl' ? (
            <Rect
              x={w - 5} y={0} width={5} height={h}
              fill={accentColor}
              cornerRadius={[0, 10, 10, 0]}
              opacity={isBoundaryNode ? 0.5 : 1}
              listening={false} perfectDrawEnabled={false}
            />
          ) : (
            <Rect
              x={0} y={0} width={5} height={h}
              fill={accentColor}
              cornerRadius={[10, 0, 0, 10]}
              opacity={isBoundaryNode ? 0.5 : 1}
              listening={false} perfectDrawEnabled={false}
            />
          )}

          {/* Top highlight */}
          {!isBoundaryNode && (
            <Rect
              x={5} y={0} width={w - 5} height={1}
              fill={palette.nodeHighlight}
              listening={false} perfectDrawEnabled={false}
            />
          )}
        </>
      )}

      {/* Type label — hidden for sketch / kit shapes (shape is self-evident) */}
      {!isSketchShapeNode && !isKitShape(node.type) && (
        <Text
          x={labelX} y={isShaped ? h / 2 - 18 : 12}
          width={labelW}
          text={typeLabel.toUpperCase()}
          fontSize={fs(8)} fontFamily={FONT_FAMILY} fontStyle="bold"
          fill={isShaped ? palette.nodeSubText : accentColor}
          opacity={isShaped ? 0.7 : 0.9}
          align={labelAlign}
          listening={false} perfectDrawEnabled={false}
        />
      )}

      {/* Node label — hidden while DOM overlay input is active */}
      {!hideLabel && !isClassifier && (wrapLabelBox ? (
        <HaloText
          x={wrapLabelBox.x}
          y={wrapLabelBox.y}
          width={wrapLabelBox.width}
          height={wrapLabelBox.height}
          text={node.label}
          fontSize={fs(SKETCH_LABEL.fontSize)}
          fontFamily={FONT_FAMILY}
          fontStyle="600"
          fill={palette.nodeText}
          align="center"
          verticalAlign="middle"
          wrap="word"
          haloFill={labelHalo}
          lineHeightPx={fs(SKETCH_LABEL.lineHeight)}
          onDblClick={handleLabelDblClick}
        />
      ) : (
        <HaloText
          x={labelX}
          y={node.type === 'uml_actor' ? h - 18 : isShaped ? h / 2 - 6 : 26}
          width={labelW}
          height={18}
          text={node.label}
          fontSize={fs(node.type === 'uml_actor' ? 11 : 13)}
          fontFamily={FONT_FAMILY}
          fontStyle="600"
          fill={palette.nodeText}
          align={isShaped ? 'center' : labelAlign}
          verticalAlign="middle"
          ellipsis
          haloFill={isShaped ? labelHalo : toRgba(palette.nodeBody, LABEL_HALO_ALPHA)}
          lineHeightPx={fs(node.type === 'uml_actor' ? 14 : 18)}
          padX={isShaped ? 6 : 4}
          padY={2}
          onDblClick={handleLabelDblClick}
        />
      ))}

      {isClassifier && !hideLabel && (() => {
        const { stereotype, attributes, methods } = classifierLines(node);
        const headerH = stereotype ? 36 : 24;
        const attrH = Math.max(22, attributes.length * 15 + 8);
        const yAttr = headerH;
        const yMeth = headerH + attrH;
        return (
          <>
            {stereotype ? (
              <Text x={6} y={4} width={w - 12} height={12} text={`«${stereotype}»`} fontSize={9} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText} align="center" listening={false} perfectDrawEnabled={false} />
            ) : null}
            <Text x={6} y={stereotype ? 16 : 6} width={w - 12} height={16} text={node.label} fontSize={13} fontFamily={FONT_FAMILY} fontStyle="600"
              fill={palette.nodeText} align="center" ellipsis onDblClick={handleLabelDblClick} perfectDrawEnabled={false} />
            <Rect x={1} y={yAttr} width={w - 2} height={1} fill={accentColor} opacity={0.45} listening={false} perfectDrawEnabled={false} />
            {attributes.map((line, i) => (
              <Text key={`a-${i}`} x={8} y={yAttr + 4 + i * 15} width={w - 16} height={15} text={line} fontSize={10} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText} ellipsis listening={false} perfectDrawEnabled={false} />
            ))}
            <Rect x={1} y={yMeth} width={w - 2} height={1} fill={accentColor} opacity={0.45} listening={false} perfectDrawEnabled={false} />
            {methods.map((line, i) => (
              <Text key={`m-${i}`} x={8} y={yMeth + 4 + i * 15} width={w - 16} height={15} text={line} fontSize={10} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText} ellipsis listening={false} perfectDrawEnabled={false} />
            ))}
          </>
        );
      })()}

      {/* Description subtitle removed — shown only in the bottom section to avoid duplication */}

      {/* Socket labels — rectangular nodes only; position depends on node direction */}
      {!isShaped && (socketLabelVisibility === 'always' || (socketLabelVisibility === 'hover' && isHovered)) && (() => {
        if (nodeDirection === 'ttb') {
          return [...inputs, ...outputs].map((socket) => socket.label && socket.id !== editingSocketId ? (
            <Text
              key={`lbl-${socket.id}`}
              x={socket.position.x - 4}
              y={socket.type === 'input' ? 8 : h - 40}
              width={32} height={10}
              text={socket.label}
              fontSize={fs(9)} fontFamily={FONT_FAMILY}
              fill={palette.nodeSubText}
              align="center"
              rotation={-90}
              listening={false} perfectDrawEnabled={false}
            />
          ) : null);
        }
        // LTR / RTL — keep standard horizontal layout but flip sides for RTL
        const inX = nodeDirection === 'rtl' ? halfW : 12;
        const inAlign = nodeDirection === 'rtl' ? 'right' : 'left';
        const outX = nodeDirection === 'rtl' ? 12 : halfW;
        const outAlign = nodeDirection === 'rtl' ? 'left' : 'right';
        return (
          <>
            {inputs.map((socket) => socket.label && socket.id !== editingSocketId ? (
              <Text
                key={`lbl-${socket.id}`}
                x={inX} y={socket.position.y - 6}
                width={halfW - 16}
                text={socket.label}
                fontSize={fs(10)} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText} align={inAlign}
                listening={false} perfectDrawEnabled={false}
              />
            ) : null)}
            {outputs.map((socket) => socket.label && socket.id !== editingSocketId ? (
              <Text
                key={`lbl-${socket.id}`}
                x={outX} y={socket.position.y - 6}
                width={halfW - 8}
                height={14}
                text={socket.label}
                fontSize={fs(10)} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText} align={outAlign}
                ellipsis
                listening={false} perfectDrawEnabled={false}
              />
            ) : null)}
          </>
        );
      })()}

      {/* API Endpoint list (api nodes only, rectangular) */}
      {!isShaped && endpoints.length > 0 && (() => {
        const socketRows = Math.max(nIn, nOut, 1);
        const epStartY = 48 + socketRows * 22 + 14;
        return (
          <>
            <Rect
              x={5} y={epStartY - 2} width={w - 5} height={1}
              fill={accentColor + '30'}
              listening={false} perfectDrawEnabled={false}
            />
            {endpoints.map((ep, i) => (
              <Text
                key={`ep-${i}`}
                x={14} y={epStartY + 4 + i * ENDPOINT_ROW}
                width={w - 28}
                text={ep}
                fontSize={10} fontFamily={FONT_FAMILY}
                fill={palette.nodeSubText}
                ellipsis
                listening={false} perfectDrawEnabled={false}
              />
            ))}
          </>
        );
      })()}

      {/* Type badge icon — only for rectangular nodes */}
      {!isShaped && (
        <NodeIcon type={node.type} accentColor={accentColor} x={w} y={0} iconStyle={iconStyle} />
      )}

      {/* Persistent indicator badges: group and/or linked-page */}
      {!isShaped && !isBoundaryNode && (isGroupNode || hasLinkedPage) && (
        <Text
          x={w - (isGroupNode && hasLinkedPage ? 28 : 16)}
          y={h - 14}
          text={[isGroupNode ? 'G' : '', hasLinkedPage ? 'P' : ''].filter(Boolean).join(' ')}
          fontSize={9}
          fontFamily={FONT_FAMILY}
          fill={isGroupNode ? '#818cf8' : '#10b981'}
          opacity={0.7}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}

      {/* Description section — rectangular nodes only, only when there's room */}
      {hasRoomForDescSection && (
        <>
          <Rect
            x={5} y={baseH - 1} width={w - 5} height={1}
            fill={accentColor + '40'}
            listening={false} perfectDrawEnabled={false}
          />
          <Rect
            x={0} y={baseH} width={w} height={descSectionHeight}
            fill={accentColor + '0a'}
            cornerRadius={[0, 0, 10, 10]}
            listening={false} perfectDrawEnabled={false}
          />
          <Text
            x={10} y={baseH + DESC_TOP_PAD}
            width={w - 20} height={descTextHeight}
            text={node.meta!.description}
            fontSize={10} fontFamily={FONT_FAMILY}
            fill={palette.nodeSubText}
            wrap="word"
            ellipsis
            onDblClick={handleDescDblClick}
            perfectDrawEnabled={false}
          />
        </>
      )}

      {/* NodeActionBar — parked just outside the top-right so it doesn't cover content */}
      {showActionBar && (
        <NodeActionBar
          x={w}
          y={0}
          nodeId={node.id}
          isGroup={isGroupNode}
          hasLinkedPage={hasLinkedPage}
          palette={palette}
          accentColor={accentColor}
          onInfo={() => onActionInfo?.(node.id)}
          onColor={() => onActionColor?.(node.id)}
          onEnterGroup={isGroupNode ? () => onActionEnterGroup?.(node.id) : undefined}
          onPortalNavigate={hasLinkedPage ? () => onActionPortalNavigate?.(node.id) : undefined}
          onLinkPage={() => onActionLinkPage?.(node.id)}
        />
      )}

      {/* Resize handle on the BR corner; hidden while connecting so sockets/ghost stay clear */}
      {showResizeHandle && (
        <ResizeHandle
          nodeWidth={w}
          nodeHeight={h}
          accentColor={accentColor}
          selected={isSelected}
          idleHint={showsIdleResizeHint(node.type)}
          nudgeId={isStackableDrawing(node.type) ? 'resize-drawing' : locksEqualAspect(node.type) ? undefined : 'resize-shape'}
          onDragStart={() => {
            resizeOrigin.current = { w: node.size.width, h: node.size.height };
          }}
          onDragEnd={() => {
            resizeOrigin.current = null;
          }}
          onResize={(rawW, rawH, mods) => {
            const stackable = isStackableDrawing(node.type);
            const fromCenter = stackable && mods.altKey;
            const cascade = stackable && isDrawCascadeModifier(mods);
            const proportional = mods.shiftKey || (!stackable && mods.altKey) || locksEqualAspect(node.type);
            let width = rawW;
            let height = rawH;
            if (fromCenter && resizeOrigin.current) {
              const symmetric = symmetricHandleSize(resizeOrigin.current, rawW, rawH, { x: true, y: true });
              width = symmetric.width;
              height = symmetric.height;
            }
            const next = constrainedResize(
              node.type,
              node.label,
              width,
              height,
              resizeOrigin.current,
              proportional,
            );
            onNodeResize?.(node.id, next.width, next.height, { fromCenter, cascade });
            return next;
          }}
        />
      )}

      {node.sockets.map((socket) => {
        const connected = connectedSockets.has(`${node.id}-${socket.id}`);
        // Sockets are an interaction affordance, not part of the picture. On a
        // node with a wide fan, drawing one ring per edge buries the node it
        // belongs to — the edges carry their own endpoint caps instead.
        if (!isConnecting && !isHovered && !isSelected) return null;
        return (
          <SocketComponent
            key={socket.id}
            socket={socket}
            nodeId={node.id}
            isConnected={connected}
            isConnecting={isConnecting}
            accentColor={accentColor}
            onSocketMouseDown={onSocketMouseDown}
            onSocketMouseUp={onSocketMouseUp}
            onSocketDoubleClick={onSocketDoubleClick}
            onSocketSlideStart={isMagneticShapeNode ? onSocketSlideStart : undefined}
            palette={palette}
            neutralUntilConnected={isMagneticShapeNode}
            connectedAs={socketRoles?.get(`${node.id}-${socket.id}`) ?? null}
          />
        );
      })}

      {ghostType && !isReadOnly && onGhostSocketDrop && (
        <GhostSocket
          x={ghostX}
          y={ghostY}
          ghostType={ghostType}
          nodeId={node.id}
          onGhostSocketDrop={onGhostSocketDrop}
        />
      )}
    </Group>
  );
};
